import { useMemo, useReducer } from "react";
import type { Dispatch, SetStateAction } from "react";
import type { StoryGeneralData, StoryEnvironmentData, StorySocialData, StoryGovernanceData } from "./types";

type SustainabilityStoryState = {
  storyGeneralData: StoryGeneralData;
  storyEnvironmentData: StoryEnvironmentData;
  storySocialData: StorySocialData;
  storyGovernanceData: StoryGovernanceData;
};

type SustainabilityStoryAction =
  | { type: "setStoryGeneralData"; payload: SetStateAction<StoryGeneralData> }
  | { type: "updateStoryGeneralData"; payload: Partial<StoryGeneralData> }
  | { type: "setStoryEnvironmentData"; payload: SetStateAction<StoryEnvironmentData> }
  | { type: "updateStoryEnvironmentData"; payload: Partial<StoryEnvironmentData> }
  | { type: "setStorySocialData"; payload: SetStateAction<StorySocialData> }
  | { type: "updateStorySocialData"; payload: Partial<StorySocialData> }
  | { type: "setStoryGovernanceData"; payload: SetStateAction<StoryGovernanceData> }
  | { type: "updateStoryGovernanceData"; payload: Partial<StoryGovernanceData> };

const initialStoryGeneralData: StoryGeneralData = {
  coverImageUrl: "",
  leadershipSignedBy: "",
  leadershipTitle: "",
  leadershipSignatureDate: "",
  leadershipPhotoUrl: "",
  leadershipIntroduction: "",
  leadershipKeyWins: "",
  leadershipAcknowledgements: "",
  leadershipLookingAhead: "",
  basisReportScope: "",
  basisLimitations: "",
  basisCertifications: "",
  practicesSustainabilityManagement: "",
  practicesConcretePractices: "",
  practicesTopInitiatives: "",
  strategyHotspots: "",
  strategyGoals: "",
  strategyPriorities: "",
  strategyFlagshipInitiatives: "",
  strategyChallenges: "",
};

const initialStoryEnvironmentData: StoryEnvironmentData = {
  b3EmissionsDrivers: "",
  b3ReductionActions: "",
  b3NextPriorities: "",
  b3DataLimitations: "",
  b4MonitoringProcess: "",
  b4PlannedImprovements: "",
  b5MaterialityExplanation: "",
  b5ValueChainImpacts: "",
  b5ActionsToReduce: "",
  b6WaterUseDescription: "",
  b6WaterIssues: "",
  b6WaterImprovements: "",
  b7WasteManagement: "",
  b7HazardousWaste: "",
  b7CircularityImprovements: "",
  c3Targets: [],
  c4PhysicalRisks: "",
  c4TransitionRisks: "",
  c4RiskAssessment: "",
  c4AdaptationActions: "",
};

const initialStorySocialData: StorySocialData = {
  // B8 Workforce
  b8WorkforceProfile: "",
  b8WorkforcePriorities: "",
  b8EmployeeSupportActions: "",
  // B9 Health & Safety
  b9HealthSafetyApproach: "",
  b9PreventionMeasures: "",
  b9IssuesImprovements: "",
  // B10 Remuneration/Training
  b10FairPayTrainingApproach: "",
  b10TrainingPriorities: "",
  b10NextYearImprovements: "",
  // C5 Additional Workforce & Diversity
  c5DiversityInclusionPolicies: "",
  c5ContractorReliance: "",
  c5ContractorManagement: "",
  c5DataLimitations: "",
  // C6 Human Rights Policies
  c6HumanRightsApproach: "",
  c6HumanRightsRisks: "",
  c6ControlsInPlace: "",
  // C7 Severe Incidents
  c7WorkforceIncidentsNarrative: "",
  c7ValueChainIncidentsNarrative: "",
};

const initialStoryGovernanceData: StoryGovernanceData = {
  // B11 Business Conduct
  b11BusinessConductApproach: "",
  b11MisconductPrevention: "",
  b11NotableUpdates: "",
  // C8 Sensitive Sectors / Exclusions
  c8SensitiveSectors: "",
  c8Exclusions: "",
  c8ExclusionsPractice: "",
  // C9 Governance Diversity
  c9GovernanceStructure: "",
  c9DiversityInclusion: "",
  c9ImprovementsGoals: "",
};

const initialState: SustainabilityStoryState = {
  storyGeneralData: initialStoryGeneralData,
  storyEnvironmentData: initialStoryEnvironmentData,
  storySocialData: initialStorySocialData,
  storyGovernanceData: initialStoryGovernanceData,
};

function resolveSetState<T>(value: SetStateAction<T>, current: T): T {
  return typeof value === "function" ? (value as (prev: T) => T)(current) : value;
}

function reducer(state: SustainabilityStoryState, action: SustainabilityStoryAction): SustainabilityStoryState {
  switch (action.type) {
    case "setStoryGeneralData":
      return { ...state, storyGeneralData: resolveSetState(action.payload, state.storyGeneralData) };
    case "updateStoryGeneralData":
      return { ...state, storyGeneralData: { ...state.storyGeneralData, ...action.payload } };
    case "setStoryEnvironmentData":
      return { ...state, storyEnvironmentData: resolveSetState(action.payload, state.storyEnvironmentData) };
    case "updateStoryEnvironmentData":
      return { ...state, storyEnvironmentData: { ...state.storyEnvironmentData, ...action.payload } };
    case "setStorySocialData":
      return { ...state, storySocialData: resolveSetState(action.payload, state.storySocialData) };
    case "updateStorySocialData":
      return { ...state, storySocialData: { ...state.storySocialData, ...action.payload } };
    case "setStoryGovernanceData":
      return { ...state, storyGovernanceData: resolveSetState(action.payload, state.storyGovernanceData) };
    case "updateStoryGovernanceData":
      return { ...state, storyGovernanceData: { ...state.storyGovernanceData, ...action.payload } };
    default:
      return state;
  }
}

type SustainabilityStoryActions = {
  setStoryGeneralData: Dispatch<SetStateAction<StoryGeneralData>>;
  updateStoryGeneralData: (updates: Partial<StoryGeneralData>) => void;
  setStoryEnvironmentData: Dispatch<SetStateAction<StoryEnvironmentData>>;
  updateStoryEnvironmentData: (updates: Partial<StoryEnvironmentData>) => void;
  setStorySocialData: Dispatch<SetStateAction<StorySocialData>>;
  updateStorySocialData: (updates: Partial<StorySocialData>) => void;
  setStoryGovernanceData: Dispatch<SetStateAction<StoryGovernanceData>>;
  updateStoryGovernanceData: (updates: Partial<StoryGovernanceData>) => void;
};

export function useSustainabilityStoryState(): { state: SustainabilityStoryState; actions: SustainabilityStoryActions } {
  const [state, dispatch] = useReducer(reducer, initialState);

  const actions = useMemo<SustainabilityStoryActions>(
    () => ({
      setStoryGeneralData: (value) => dispatch({ type: "setStoryGeneralData", payload: value }),
      updateStoryGeneralData: (updates) => dispatch({ type: "updateStoryGeneralData", payload: updates }),
      setStoryEnvironmentData: (value) => dispatch({ type: "setStoryEnvironmentData", payload: value }),
      updateStoryEnvironmentData: (updates) => dispatch({ type: "updateStoryEnvironmentData", payload: updates }),
      setStorySocialData: (value) => dispatch({ type: "setStorySocialData", payload: value }),
      updateStorySocialData: (updates) => dispatch({ type: "updateStorySocialData", payload: updates }),
      setStoryGovernanceData: (value) => dispatch({ type: "setStoryGovernanceData", payload: value }),
      updateStoryGovernanceData: (updates) => dispatch({ type: "updateStoryGovernanceData", payload: updates }),
    }),
    []
  );

  return { state, actions };
}
